import React, { useState } from 'react'
import type { WizardState } from './types'
import { BLUE, SectionBlock, RadioPillList, Expandable, FieldRow, Toggle, inputStyle } from './ui'

// ─── Section icons ────────────────────────────────────────────────────────────
const IcoFlame = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 3z"/>
  </svg>
)
const IcoDrop = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
  </svg>
)
const IcoZap = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
)
const IcoSnow = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="2" y1="12" x2="22" y2="12"/>
    <line x1="12" y1="2" x2="12" y2="22"/>
    <path d="M20 16l-4-4 4-4M4 8l4 4-4 4M16 4l-4 4-4-4M8 20l4-4 4 4"/>
  </svg>
)
const IcoTv = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="15" rx="2" ry="2"/>
    <polyline points="17 2 12 7 7 2"/>
  </svg>
)
const IcoPeople = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
)
const IcoList = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/>
    <line x1="8" y1="12" x2="21" y2="12"/>
    <line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/>
    <line x1="3" y1="12" x2="3.01" y2="12"/>
    <line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
)
const IcoReceipt = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 2 3 2 3 22 21 22 21 2 18 2"/>
    <path d="M6 2h12a2 2 0 0 1 2 2v1H4V4a2 2 0 0 1 2-2z"/>
    <line x1="9" y1="14" x2="15" y2="14"/>
    <line x1="9" y1="18" x2="12" y2="18"/>
  </svg>
)

// ─── Data ─────────────────────────────────────────────────────────────────────
const VARME_OPTIONS = [
  { label: 'A conto til udlejer', value: 'aconto', badge: 'Mest almindeligt' },
  { label: 'Inkluderet i huslejen', value: 'inkluderet' },
  { label: 'Mellem lejer og forsyningsselskab', value: 'lejer' },
]

const VAND_OPTIONS = [
  { label: 'A conto til udlejer', value: 'aconto', badge: 'Mest almindeligt' },
  { label: 'Inkluderet i huslejen', value: 'inkluderet' },
  { label: 'Mellem lejer og forsyningsselskab', value: 'lejer' },
]

const EL_OPTIONS = [
  { label: 'A conto til udlejer', value: 'aconto' },
  { label: 'Inkluderet i huslejen', value: 'inkluderet' },
  { label: 'Mellem lejer og forsyningsselskab', value: 'lejer', badge: 'Mest almindeligt' },
]

const KOLING_OPTIONS = [
  { label: 'A conto til udlejer', value: 'aconto' },
  { label: 'Inkluderet i huslejen', value: 'inkluderet' },
  { label: 'Der tilbydes ikke køling', value: 'ingen', badge: 'Mest almindeligt' },
]

// Matches Rentdesk: El til opvarmning / Centralvarme / Fjernvarme+naturgas / Andet
const VARMEKILDE = [
  { label: 'El til opvarmning', value: 'el' },
  { label: 'Centralvarme', value: 'centralvarme' },
  { label: 'Fjernvarme / naturgas', value: 'fjernvarme' },
  { label: 'Andet', value: 'andet' },
]

const MONTHS = [
  'Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'December',
]

// ─── Helpers ──────────────────────────────────────────────────────────────────
function parseMD(s: string): [string, string] {
  const parts = (s || '').split('-')
  // Handle both "MM-DD" and legacy "YYYY-MM-DD" formats
  if (parts.length >= 3) return [parts[1] || '01', parts[2] || '01']
  return [parts[0] || '01', parts[1] || '01']
}
function mkMD(mm: string, dd: string) {
  return `${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`
}

// ─── AcontoField ──────────────────────────────────────────────────────────────
function AcontoField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <FieldRow label={label}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input style={{ ...inputStyle, flex: 1 }} type="number" value={value} onChange={e => onChange(e.target.value)} />
        <span style={{ fontSize: 13, color: '#9ca3af', flexShrink: 0 }}>kr./md.</span>
      </div>
    </FieldRow>
  )
}

// ─── DatoField (day + month pickers) ─────────────────────────────────────────
function DatoField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const [mm, dd] = parseMD(value)
  return (
    <FieldRow label={label}>
      <div style={{ display: 'flex', gap: 6 }}>
        <select style={{ ...inputStyle, width: 72 }}
          value={dd}
          onChange={e => onChange(mkMD(mm, e.target.value))}>
          {Array.from({ length: 28 }, (_, i) => (
            <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}.</option>
          ))}
        </select>
        <select style={{ ...inputStyle, flex: 1 }}
          value={mm}
          onChange={e => onChange(mkMD(e.target.value, dd))}>
          {MONTHS.map((m, i) => (
            <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{m}</option>
          ))}
        </select>
      </div>
    </FieldRow>
  )
}

// ─── WarnBox (amber) ─────────────────────────────────────────────────────────
function WarnBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7,
      padding: '10px 12px', fontSize: 12, color: '#92400e', lineHeight: 1.55, marginBottom: 8,
    }}>
      {children}
    </div>
  )
}

// ─── ForbrugSummaryRow ────────────────────────────────────────────────────────
function ForbrugSummaryRow({ label, dkk, icon }: { label: string; dkk: string; icon: React.ReactNode }) {
  const val = parseFloat(dkk || '0') || 0
  if (!val) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
        {icon}
      </div>
      <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{val.toFixed(2).replace('.', ',')} kr./md.</span>
    </div>
  )
}

// ─── ElConfirmModal ───────────────────────────────────────────────────────────
function ElConfirmModal({ type, onConfirm, onSwitch }: {
  type: 'inkluderet' | 'aconto'
  onConfirm: () => void
  onSwitch: () => void
}) {
  const isInkl = type === 'inkluderet'
  const title = isInkl
    ? 'Er du sikker på at elforbruget skal være Inkluderet i huslejen?'
    : 'Er du sikker på at elforbruget skal være A conto til udlejer?'
  const confirmLabel = isInkl ? 'Vælg Inkluderet i huslejen' : 'Vælg A conto til udlejer'

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 500, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#0f172a', lineHeight: 1.4, flex: 1 }}>{title}</h3>
          <button onClick={onSwitch} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', marginLeft: 12, padding: 0 }}>×</button>
        </div>
        <p style={{ fontSize: 13, color: '#374151', marginBottom: 16, lineHeight: 1.6 }}>
          Du er ved at vælge en valgmulighed som ikke anbefales for din type lejemål. Det anbefalede valg for din type lejemål er, at lejeren afregner elforbruget direkte med forsyningsselskabet.
        </p>
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 8 }}>
            {isInkl
              ? 'Vigtigt at bemærke hvis du vælger at elforbruget er inkluderet i huslejen'
              : 'Vigtigt at bemærke hvis du vælger at afregne elforbrug via A conto til udlejer'}
          </div>
          {isInkl ? (
            <div style={{ display: 'flex', gap: 8, fontSize: 13, color: '#78350f', lineHeight: 1.5 }}>
              <span>⚠️</span>
              <span>Hvis lejerens faktiske elforbrug ender ud i omkostninger der er større end beløbet du inkluderede i lejen, må du ifølge Lejeloven ikke kræve efterbetaling. Du som udlejer hænger derfor på ekstraomkostningerne</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                'Udgiften til el skal ifølge lejeloven fordeles på grundlag af individuelle forbrugsmålere',
                'Du er ifølge Lejeloven påkrævet at udføre og tilgængeliggøre årlige forbrugsregnskaber til lejeren, hvor forholdet mellem det indbetalte A conto beløb og det reelle forbrug opgøres. Dette skal gøres under hele lejeforholdets varighed',
                'Hvis A conto beløbet er højere end det egentlige forbrug, er du påkrævet at tilbagebetale eller modregne differencen i lejerens fremtidige betalinger',
                'Hvis det månedlige A conto beløb senere viser sig at skulle justeres, skal du ifølge Lejeloven give lejer 6 ugers varsel, før du må justere beløbet',
              ].map((text, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12, color: '#78350f', lineHeight: 1.5 }}>
                  <span style={{ flexShrink: 0 }}>⚠️</span>
                  <span>{text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <button onClick={onConfirm} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' }}>
            {confirmLabel}
          </button>
          <button onClick={onSwitch} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Skift til anbefalet valg
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── VarmekildeField ──────────────────────────────────────────────────────────
function VarmekildeField({ value, onChange, required, otherValue, onOtherChange }: {
  value: string; onChange: (v: string) => void; required?: boolean
  otherValue: string; onOtherChange: (v: string) => void
}) {
  return (
    <>
      <FieldRow label="Lejemålet er opvarmet ved" required={required}>
        <RadioPillList options={VARMEKILDE} value={value} onChange={onChange} />
      </FieldRow>
      {value === 'andet' && (
        <FieldRow label="Beskriv varmekilden" required={required}>
          <input style={inputStyle} value={otherValue}
            onChange={e => onOtherChange(e.target.value)}
            placeholder="F.eks. brændeovn, jordvarme…" />
        </FieldRow>
      )}
    </>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function deriveHeatingProvider(billing: string | null, heatingType: string): WizardState['heating_provider'] {
  if (billing === 'lejer') return 'lejer'
  if (billing === 'aconto' || billing === 'inkluderet') {
    return (heatingType as WizardState['heating_provider']) || 'udlejer'
  }
  return ''
}

// ─── SectionForbrug ───────────────────────────────────────────────────────────
export function SectionForbrug({ state, onChange }: { state: WizardState; onChange: (p: Partial<WizardState>) => void }) {
  const [elPendingType, setElPendingType] = useState<'inkluderet' | 'aconto' | null>(null)

  const setHeatingBilling = (v: string) => {
    const billing = v as WizardState['heating_billing_type']
    // Clear the a conto amount + regnskabsår when the section that holds them is
    // hidden, so the form state stays the single source of truth for the PDF.
    const cleared = billing === 'aconto' ? {} : { aconto_heating_dkk: '', aconto_heating_index_start: '' }
    onChange({ heating_billing_type: billing, heating_provider: deriveHeatingProvider(billing, state.heating_type), ...cleared })
  }

  const setHeatingType = (v: string) => {
    onChange({ heating_type: v, heating_provider: deriveHeatingProvider(state.heating_billing_type, v) })
  }

  const setElBilling = (v: string) => {
    const billing = v as WizardState['el_billing_type']
    if (billing === 'inkluderet' || billing === 'aconto') {
      setElPendingType(billing)
    } else {
      onChange({
        el_billing_type: billing,
        electricity_provider: billing === 'lejer' ? 'lejer' : 'udlejer',
        aconto_electricity_dkk: '', aconto_el_start_date: '',
      })
    }
  }

  const totalDkk = [
    state.aconto_heating_dkk, state.aconto_water_dkk, state.aconto_electricity_dkk,
    state.aconto_cooling_dkk, state.aconto_antenna_dkk, state.aconto_internet_dkk,
    state.beboer_amount_dkk,
    ...(state.extra_contributions ?? []).map(c => c.dkk),
  ].reduce((sum, v) => sum + (parseFloat(v || '0') || 0), 0)

  return (
    <div>
      {elPendingType && (
        <ElConfirmModal
          type={elPendingType}
          onConfirm={() => {
            // 'inkluderet' hides the a conto fields → clear them; 'aconto' keeps them.
            const cleared = elPendingType === 'aconto' ? {} : { aconto_electricity_dkk: '', aconto_el_start_date: '' }
            onChange({ el_billing_type: elPendingType, electricity_provider: 'udlejer', ...cleared })
            setElPendingType(null)
          }}
          onSwitch={() => {
            onChange({ el_billing_type: 'lejer', electricity_provider: 'lejer', aconto_electricity_dkk: '', aconto_el_start_date: '' })
            setElPendingType(null)
          }}
        />
      )}

      {/* ── Varme & varmt vand ── */}
      <SectionBlock icon={IcoFlame} title="Varme & varmt vand" subtitle="Hvordan skal varmeforbruget afregnes?">
        <RadioPillList options={VARME_OPTIONS} value={state.heating_billing_type ?? ''}
          onChange={setHeatingBilling} />

        {/* A conto */}
        <Expandable open={state.heating_billing_type === 'aconto'}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <AcontoField label="Månedligt beløb" value={state.aconto_heating_dkk}
              onChange={v => onChange({ aconto_heating_dkk: v })} />
            <DatoField label="Varmeregnskabsåret starter d."
              value={state.aconto_heating_index_start}
              onChange={v => onChange({ aconto_heating_index_start: v })} />
          </div>
          <VarmekildeField value={state.heating_type} onChange={setHeatingType} required
            otherValue={state.heating_type_other} onOtherChange={v => onChange({ heating_type_other: v })} />
        </Expandable>

        {/* Inkluderet */}
        <Expandable open={state.heating_billing_type === 'inkluderet'}>
          <VarmekildeField value={state.heating_type} onChange={setHeatingType} required
            otherValue={state.heating_type_other} onOtherChange={v => onChange({ heating_type_other: v })} />
        </Expandable>

        {/* Lejer */}
        <Expandable open={state.heating_billing_type === 'lejer'}>
          <VarmekildeField value={state.heating_type} onChange={setHeatingType} required
            otherValue={state.heating_type_other} onOtherChange={v => onChange({ heating_type_other: v })} />
        </Expandable>
      </SectionBlock>

      {/* ── Vand ── */}
      <SectionBlock icon={IcoDrop} title="Vand" subtitle="Hvordan skal vandforbruget afregnes?">
        <RadioPillList options={VAND_OPTIONS} value={state.water_billing_type ?? ''}
          onChange={v => onChange({
            water_billing_type: v as WizardState['water_billing_type'],
            water_provider: v === 'lejer' ? 'lejer' : 'udlejer',
            ...(v === 'aconto'
              ? { vand_individuelt_maaler: true }
              : { aconto_water_dkk: '', aconto_water_index_start: '', vand_individuelt_maaler: false }),
          })} />

        {/* A conto */}
        <Expandable open={state.water_billing_type === 'aconto'}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <AcontoField label="Månedligt beløb" value={state.aconto_water_dkk}
              onChange={v => onChange({ aconto_water_dkk: v })} />
            <DatoField label="Vandregnskabsåret starter d."
              value={state.aconto_water_index_start}
              onChange={v => onChange({ aconto_water_index_start: v })} />
          </div>
          <FieldRow label="">
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox"
                checked={state.vand_individuelt_maaler}
                onChange={e => onChange({ vand_individuelt_maaler: e.target.checked })}
                style={{ width: 16, height: 16, accentColor: BLUE, cursor: 'pointer' }} />
              <span style={{ fontSize: 13, color: '#374151' }}>
                Vand fordeles på grundlag af individuelle forbrugsmålere
              </span>
            </label>
          </FieldRow>
          {!state.vand_individuelt_maaler && (
            <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 7, fontSize: 12, color: '#9a3412' }}>
              Det er ikke tilladt at have vand på a conto uden individuelle forbrugsmålere (lejelovens §46a).
            </div>
          )}
        </Expandable>
      </SectionBlock>

      {/* ── El ── */}
      <SectionBlock icon={IcoZap} title="El" subtitle="Hvordan skal elforbruget afregnes?">
        <RadioPillList options={EL_OPTIONS} value={state.el_billing_type ?? ''} onChange={setElBilling} />

        {/* A conto */}
        <Expandable open={state.el_billing_type === 'aconto'}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <AcontoField label="Månedligt beløb" value={state.aconto_electricity_dkk}
              onChange={v => onChange({ aconto_electricity_dkk: v })} />
            <DatoField label="Startdato for levering af el"
              value={state.aconto_el_start_date}
              onChange={v => onChange({ aconto_el_start_date: v })} />
          </div>
          <WarnBox>
            Bemærk at der ifølge lejeloven kun må opkræves a conto, hvis udgiften til el fordeles på grundlag af individuelle forbrugsmålere, samt at der udføres et årligt forbrugsregnskab.
          </WarnBox>
        </Expandable>

        {/* Inkluderet */}
        <Expandable open={state.el_billing_type === 'inkluderet'}>
          <WarnBox>
            Bemærk at hvis lejerens faktiske forbrug er større end det forventede beløb som du inkluderede i huslejen, må du ifølge Lejeloven ikke kræve efterbetaling.
          </WarnBox>
        </Expandable>

        {/* Lejer / Mellem lejer og forsyningsselskab */}
        <Expandable open={state.el_billing_type === 'lejer'}>
          <div style={{
            background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 7,
            padding: '12px 14px', marginBottom: 8,
          }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%', background: BLUE, flexShrink: 0, marginTop: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5"><polyline points="2 6 5 9 10 3"/></svg>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#4f46e5', marginBottom: 3 }}>
                  Automatisk tilmelding af lejer til el
                </div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                  Lejeren tilmeldes automatisk elforsyningen, så du undgår at hænge på lejers elregning og administrativt bøvl. Tilmelding foregår automatisk via NRGi, Danmarks bedst anmeldte elselskab, og er gratis for både dig og lejeren.
                </div>
              </div>
            </div>
          </div>
        </Expandable>
      </SectionBlock>

      {/* ── Køling ── */}
      <SectionBlock icon={IcoSnow} title="Køling" subtitle="Hvordan skal kølingsforbruget afregnes?">
        <RadioPillList options={KOLING_OPTIONS} value={state.cooling_billing_type ?? 'ingen'}
          onChange={v => onChange({
            cooling_billing_type: v as WizardState['cooling_billing_type'],
            cooling_provider: v === 'aconto' || v === 'inkluderet' ? 'udlejer' : '',
            ...(v === 'aconto' ? {} : { aconto_cooling_dkk: '', aconto_koling_start_date: '', koling_individuelt_maaler: false }),
          })} />

        {/* A conto */}
        <Expandable open={state.cooling_billing_type === 'aconto'}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <AcontoField label="Månedligt beløb" value={state.aconto_cooling_dkk}
              onChange={v => onChange({ aconto_cooling_dkk: v })} />
            <DatoField label="Forsyning køling start dato"
              value={state.aconto_koling_start_date}
              onChange={v => onChange({ aconto_koling_start_date: v })} />
          </div>
          <FieldRow label="">
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox"
                checked={state.koling_individuelt_maaler}
                onChange={e => onChange({ koling_individuelt_maaler: e.target.checked })}
                style={{ width: 16, height: 16, accentColor: BLUE, cursor: 'pointer' }} />
              <span style={{ fontSize: 13, color: '#374151' }}>
                Forsyning køling har individuelt måler
              </span>
            </label>
          </FieldRow>
        </Expandable>
      </SectionBlock>

      {/* ── TV & Internet ── */}
      <SectionBlock icon={IcoTv} title="TV & Internet" subtitle="Tilbydes der TV og / eller internet?">
        <FieldRow label="Delt husantenne">
          <Toggle
            on={state.antenna_type === 'faellesantenne'}
            onToggle={() => {
              const next = state.antenna_type === 'faellesantenne' ? 'ingen' : 'faellesantenne'
              onChange({
                antenna_type: next, antenna_included: next === 'faellesantenne',
                ...(next === 'faellesantenne' ? {} : { aconto_antenna_dkk: '' }),
              })
            }}
          />
        </FieldRow>
        <Expandable open={state.antenna_type === 'faellesantenne'}>
          <AcontoField label="Månedligt antennebidrag" value={state.aconto_antenna_dkk}
            onChange={v => onChange({ aconto_antenna_dkk: v })} />
        </Expandable>
        <FieldRow label="Internet leveres af udlejer">
          <Toggle
            on={state.internet_type === 'udlejer'}
            onToggle={() => {
              const next = state.internet_type === 'udlejer' ? 'ingen' : 'udlejer'
              onChange({
                internet_type: next, internet_included: next === 'udlejer',
                ...(next === 'udlejer' ? {} : { aconto_internet_dkk: '' }),
              })
            }}
          />
        </FieldRow>
        <Expandable open={state.internet_type === 'udlejer'}>
          <AcontoField label="Månedligt internetbidrag" value={state.aconto_internet_dkk}
            onChange={v => onChange({ aconto_internet_dkk: v })} />
        </Expandable>
      </SectionBlock>

      {/* ── Beboerrepræsentation ── */}
      <SectionBlock icon={IcoPeople} title="Beboerrepræsentation" subtitle="Tilbydes der beboerrepræsentation i ejendommen?">
        <FieldRow label="Der tilbydes beboerrepræsentation">
          <Toggle
            on={state.beboerrepraesentation}
            onToggle={() => {
              const next = !state.beboerrepraesentation
              onChange({ beboerrepraesentation: next, ...(next ? {} : { beboer_amount_dkk: '' }) })
            }}
          />
        </FieldRow>
        <Expandable open={state.beboerrepraesentation}>
          <AcontoField label="Månedligt bidrag til beboerrepræsentation" value={state.beboer_amount_dkk}
            onChange={v => onChange({ beboer_amount_dkk: v })} />
        </Expandable>
      </SectionBlock>

      {/* ── Andet bidrag ── */}
      <SectionBlock icon={IcoList} title="Andet bidrag" subtitle="Er der yderligere bidrag der skal betales til ejendommen?">
        {(state.extra_contributions ?? []).map((row, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <input style={{ ...inputStyle, flex: 1 }} placeholder="Navngiv bidrag…"
              value={row.name}
              onChange={e => {
                const next = [...state.extra_contributions]
                next[i] = { ...next[i], name: e.target.value }
                onChange({ extra_contributions: next })
              }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input style={{ ...inputStyle, width: 100 }} type="number" value={row.dkk}
                onChange={e => {
                  const next = [...state.extra_contributions]
                  next[i] = { ...next[i], dkk: e.target.value }
                  onChange({ extra_contributions: next })
                }} />
              <span style={{ fontSize: 13, color: '#9ca3af', flexShrink: 0 }}>kr./md.</span>
            </div>
            <button onClick={() => onChange({ extra_contributions: state.extra_contributions.filter((_, j) => j !== i) })}
              style={{ width: 32, height: 32, border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', cursor: 'pointer', color: '#9ca3af' }}>
              ×
            </button>
          </div>
        ))}
        <button
          onClick={() => onChange({ extra_contributions: [...(state.extra_contributions ?? []), { name: '', dkk: '' }] })}
          style={{ fontSize: 13, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          + Tilføj bidrag
        </button>
      </SectionBlock>

      {/* ── Opsummering ── */}
      <SectionBlock icon={IcoReceipt} title="Opsummering af forbrug & bidrag" subtitle="Overblik af hvad der betales af forbrug & bidrag hver måned">
        <div style={{ borderRadius: 8, border: '1px solid #e8eaed', overflow: 'hidden' }}>
          <div style={{ padding: '0 16px' }}>
            <ForbrugSummaryRow label="A conto varme og varmt vand" dkk={state.aconto_heating_dkk} icon={IcoFlame} />
            <ForbrugSummaryRow label="A conto vand" dkk={state.aconto_water_dkk} icon={IcoDrop} />
            <ForbrugSummaryRow label="A conto el" dkk={state.aconto_electricity_dkk} icon={IcoZap} />
            <ForbrugSummaryRow label="A conto køling" dkk={state.aconto_cooling_dkk} icon={IcoSnow} />
            <ForbrugSummaryRow label="Antennebidrag" dkk={state.aconto_antenna_dkk} icon={IcoTv} />
            <ForbrugSummaryRow label="Internetbidrag" dkk={state.aconto_internet_dkk} icon={IcoTv} />
            <ForbrugSummaryRow label="Beboerrepræsentation" dkk={state.beboer_amount_dkk} icon={IcoPeople} />
            {(state.extra_contributions ?? []).map((c, i) => (
              <ForbrugSummaryRow key={i} label={c.name || `Bidrag ${i + 1}`} dkk={c.dkk} icon={IcoList} />
            ))}
            {totalDkk === 0 && (
              <div style={{ padding: '12px 0', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>
                Ingen forbrug eller bidrag registreret
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: '#f7f8fa', borderTop: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Total pr. måned</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: totalDkk > 0 ? '#111827' : '#9ca3af' }}>
              {totalDkk.toFixed(2).replace('.', ',')} kr./md.
            </span>
          </div>
        </div>
      </SectionBlock>

    </div>
  )
}
